import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { environment } from '../../environments/environment';

/**
 * IntegrationOauthService
 * ------------------------
 * Servicio que orquesta el flujo OAuth desde el lado cliente:
 *   1. Hace POST a `/api/integrations/:provider/oauth/start` para obtener authUrl.
 *   2. Abre la authUrl en un POPUP (window.open).
 *   3. Escucha `window.postMessage` desde el callback (origen mismo SPA).
 *   4. Resuelve con `{ ok: true }` en success, `{ ok: false, error }` en error.
 *
 * También expone `getStatus(provider)` y `disconnect(provider)` para que la UI
 * (chip de conexión en el panel — Punto 7) pueda mostrar el estado y permitir
 * desconectar.
 *
 * Nota sobre el popup: usamos popup en lugar de same-window redirect porque
 * preserva el contexto del usuario (panel abierto, prompt RAG sin perder).
 * Los bloqueadores de popups pueden interferir — el usuario debe permitir
 * popups del dominio del SPA si se le pregunta.
 *
 * @author Marcos Cordo
 * @version 06/06/2026_1
 */

export type OauthProvider = 'jira' | 'gitlab';

export interface OauthStartResponse {
  authUrl: string;
  state: string;
}

export interface OauthResult {
  ok: boolean;
  provider: OauthProvider;
  error?: string;
}

export interface IntegrationStatus {
  connected: boolean;
  accountName?: string;
  accountId?: string;
  scope?: string;
  connectedAt?: string;
  expiresAt?: string;
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 720;
const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_MAX_WAIT_MS = 10 * 60 * 1000;     // 10 min — coincide con TTL del state en Redis

@Injectable({ providedIn: 'root' })
export class IntegrationOauthService {
  private http = inject(HttpClient);
  private logger = inject(LoggerService);

  /**
   * Inicia el flujo OAuth y devuelve una Promise que resuelve cuando el popup
   * notifica el resultado.
   */
  async connect(provider: OauthProvider, returnTo?: string): Promise<OauthResult> {
    this.logger.debug(`[IntegrationOauth] connect(${provider}) start.`);

    // 1. Pedir authUrl al backend.
    const body: { returnTo?: string } = {};
    if (returnTo) body.returnTo = returnTo;

    const start = await firstValueFrom(
      this.http.post<OauthStartResponse>(
        `${environment.api_service_integrations}/${provider}/oauth/start`,
        body
      )
    );

    // 2. Abrir popup centrado.
    const left = Math.max(0, (window.screen.width  - POPUP_WIDTH)  / 2);
    const top  = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);
    const features =
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},` +
      `resizable=yes,scrollbars=yes,status=no,location=yes,toolbar=no,menubar=no`;

    const popup = window.open(start.authUrl, 'oauth-popup', features);
    if (!popup) {
      this.logger.error('[IntegrationOauth] window.open devolvió null — popup bloqueado.');
      return { ok: false, provider, error: 'popup_blocked' };
    }

    // 3. Esperar postMessage del callback. Resolver al recibir o al cerrar el popup
    //    sin haber recibido nada (usuario cerró manualmente).
    return new Promise<OauthResult>((resolve) => {
      let settled = false;
      const settle = (result: OauthResult): void => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        clearInterval(closeWatcher);
        clearTimeout(maxWaitTimer);
        try { if (!popup.closed) popup.close(); } catch (_) { /* ignore */ }
        resolve(result);
      };

      const onMessage = (event: MessageEvent): void => {
        // Aceptamos solo mensajes del MISMO origin (el callback vive en el propio SPA).
        if (event.origin !== window.location.origin) return;
        const data = event.data;
        if (!data || typeof data !== 'object') return;

        if (data.type === 'oauth-success' && data.provider === provider) {
          settle({ ok: true, provider });
        } else if (data.type === 'oauth-error' && data.provider === provider) {
          settle({ ok: false, provider, error: data.error || 'unknown' });
        }
      };

      window.addEventListener('message', onMessage);

      // El usuario puede cerrar el popup manualmente sin completar nada.
      const closeWatcher = setInterval(() => {
        if (popup.closed) {
          settle({ ok: false, provider, error: 'popup_closed_by_user' });
        }
      }, POPUP_POLL_INTERVAL_MS);

      // Timeout duro — TTL del state en Redis.
      const maxWaitTimer = setTimeout(() => {
        settle({ ok: false, provider, error: 'timeout' });
      }, POPUP_MAX_WAIT_MS);
    });
  }

  /** Consulta si el usuario tiene una integración activa con el provider. */
  getStatus(provider: OauthProvider): Promise<IntegrationStatus> {
    return firstValueFrom(
      this.http.get<IntegrationStatus>(`${environment.api_service_integrations}/${provider}/status`)
    );
  }

  /** Borra la integración del usuario para el provider. */
  disconnect(provider: OauthProvider): Promise<{ disconnected: boolean }> {
    return firstValueFrom(
      this.http.delete<{ disconnected: boolean }>(`${environment.api_service_integrations}/${provider}`)
    );
  }
}
